import notfound from "assets/images/notfound.png";
import DropDownButtons from "components/Ui/DropDownButtons/DropDownButtons";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import BlockStatusButton from "components/Ui/BlockStatusButton/BlockStatusButton";
import PercentageCell from "components/Ui/PercentageCell/PercentageCell";
import DisplayRankCell from "components/Ui/DisplayRankCell/DisplayRankCell";
import WalletModeCell from "components/Ui/WalletModeCell/WalletModeCell";
import { formatMoney } from "utils/money";
import AppPresenceBadge from "components/Ui/AppPresenceBadge/AppPresenceBadge";
import MenuSimple from "./Dropdown";
import { useNavigate } from "react-router-dom";
import Select from "components/Ui/SelectField/SelectField";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectAuth } from "store/authSlice";

/**
 * Renders a vendor row with inline controls for quick operational updates.
 * Category edits are saved directly from the table to avoid forcing a full edit-page round trip.
 */
const Vendor = (props) => {
    const navigate = useNavigate();
    const { userData } = useSelector(selectAuth);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isUpdatingCategories, setIsUpdatingCategories] = useState(false);
    const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);

    const categorySelectStyles = {
        container: (base) => ({
            ...base,
            width: "100%",
        }),
        control: (base, state) => ({
            ...base,
            minHeight: "42px",
            height: "42px",
            borderRadius: "8px",
            borderColor: state.isFocused ? "#b9b5c3" : "#d8d6de",
            boxShadow: state.isFocused ? "0 3px 10px rgba(34, 41, 47, 0.08)" : "none",
            backgroundColor: isUpdatingCategories ? "#f8f8fb" : "#ffffff",
            transition: "all 0.2s ease",
            padding: "2px 6px",
            opacity: isUpdatingCategories ? 0.8 : 1,
            overflow: "hidden",
            "&:hover": {
                borderColor: "#b9b5c3",
            },
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: "nowrap",
            gap: "4px",
            padding: "1px 4px",
            overflowX: "auto",
            overflowY: "hidden",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            whiteSpace: "nowrap",
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            color: "#1f2937",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#a5a3ae",
            fontSize: "13px",
            fontWeight: 400,
            whiteSpace: "nowrap",
        }),
        multiValue: (base) => ({
            ...base,
            flexShrink: 0,
            maxWidth: "140px",
            backgroundColor: "#f3f2f7",
            border: "1px solid #ebe9f1",
            borderRadius: "6px",
            paddingInline: "2px",
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: "#5e5873",
            fontWeight: 500,
            fontSize: "12px",
            paddingInline: "6px 2px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: "#6e6b7b",
            borderRadius: "4px",
            ":hover": {
                backgroundColor: "#e7e5ec",
                color: "#5e5873",
            },
        }),
        indicatorSeparator: () => ({
            display: "none",
        }),
        dropdownIndicator: (base) => ({
            ...base,
            color: "#6e6b7b",
            paddingInline: "4px 8px",
            ":hover": {
                color: "#5e5873",
            },
        }),
        clearIndicator: (base) => ({
            ...base,
            color: "#6e6b7b",
            paddingInline: "4px",
            ":hover": {
                color: "#ea5455",
            },
        }),
        menu: (base) => ({
            ...base,
            zIndex: 20,
            marginTop: "4px",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(34, 41, 47, 0.1)",
            border: "1px solid #ebe9f1",
            padding: "4px",
            backgroundColor: "#ffffff",
        }),
        menuList: (base) => ({
            ...base,
            display: "grid",
            gap: "2px",
            padding: 0,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? "#6e6b7b" : state.isFocused ? "#f8f8fb" : "#ffffff",
            color: state.isSelected ? "#ffffff" : "#1f2937",
            fontWeight: state.isSelected ? 600 : 400,
            cursor: "pointer",
            borderRadius: "6px",
            padding: "10px 12px",
            border: "none",
        }),
        noOptionsMessage: (base) => ({
            ...base,
            color: "#a5a3ae",
            fontSize: "13px",
        }),
    };

    useEffect(() => {
        setSelectedCategories(
            (props.vendor.vendorCategories || []).map((category) => ({
                value: category.id,
                label: category.name,
            })),
        );
    }, [props.vendor.vendorCategories]);

    const saveCategories = async (categoriesToSave) => {
        const formData = new FormData();

        formData.append("id", props.vendor.id);
        formData.append("replaceCategories", "true");
        (categoriesToSave || []).forEach((category) => {
            formData.append("categories[]", category.value);
        });

        setIsUpdatingCategories(true);

        try {
            await props.updateVendor(formData);
            props.showMessage("success", "تم التعديل", "تم تعديل تصنيفات المطعم بنجاح");
        } catch {
            setSelectedCategories(
                (props.vendor.vendorCategories || []).map((category) => ({
                    value: category.id,
                    label: category.name,
                })),
            );
            props.showMessage("error", "هناك خطأ", "فشل تعديل تصنيفات المطعم");
        } finally {
            setIsUpdatingCategories(false);
        }
    };

    const updateCategories = (nextSelectedCategories) => {
        const normalizedCategories = nextSelectedCategories || [];

        setSelectedCategories(normalizedCategories);
        setIsCategoryMenuOpen(true);
        saveCategories(normalizedCategories);
    };

    const cellRenderers = {
        select: () => (
            <td>
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={props.selectedVendors.includes(props.vendor.vendorId)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            props.setSelectedVendors((prev) => [...prev, props.vendor.vendorId]);
                        } else {
                            props.setSelectedVendors((prev) =>
                                prev.filter((id) => id !== props.vendor.vendorId),
                            );
                        }
                    }}
                />
            </td>
        ),
        id: () => <td>{props.vendor?.vendorId}</td>,
        name: () => (
            <td className="name">
                <span>
                    <div>
                        <img
                            src={props.vendor.image ? props.vendor.image : notfound}
                            alt="vendor"
                        />
                    </div>
                    <div>
                        <h6>{props.vendor?.name}</h6>
                        <p>{props.vendor?.phone}</p>
                    </div>
                </span>
            </td>
        ),
        address: () => <td>{props.vendor?.address}</td>,
        city: () => <td>{props.vendor?.city ? props.vendor.city.name : "لا يوجد"}</td>,
        areas: () => <td>{props.vendor?.areas?.length} مناطق</td>,
        status: () => (
            <td>
                <MenuSimple
                    id={props.vendor.id}
                    updateVendor={props.updateVendor}
                    status={props.vendor?.status}
                    // «مفتوح» is refused while the wallet keeps it closed (api#147).
                    walletBlocked={props.vendor?.wallet?.blocked === true}
                    showMessage={props.showMessage}
                />
            </td>
        ),
        appStatus: () => (
            <td>
                <AppPresenceBadge
                    presence={props.vendor?.appPresence}
                    channel="vendorApp"
                    neverLabel="لم يفتح التطبيق"
                />
            </td>
        ),
        dashboardStatus: () => (
            <td>
                <AppPresenceBadge
                    presence={props.vendor?.appPresence}
                    channel="vendorWeb"
                    neverLabel="لم يدخل لوحة التحكم"
                />
            </td>
        ),
        schedule: () => (
            <td>
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => props.openScheduleModal(props.vendor.vendorId)}
                >
                    ⏰ المواعيد
                </button>
            </td>
        ),
        categories: () => (
            <td>
                <div className="vendor-categories-cell">
                    <Select
                        isMulti
                        isSearchable
                        isClearable
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        placeholder="اختر التصنيفات"
                        options={props.categoryOptions}
                        value={selectedCategories}
                        menuIsOpen={isCategoryMenuOpen}
                        noOptionsMessage={() => "لا يوجد تصنيفات"}
                        styles={categorySelectStyles}
                        onChange={updateCategories}
                        onMenuOpen={() => setIsCategoryMenuOpen(true)}
                        onMenuClose={() => setIsCategoryMenuOpen(false)}
                    />
                    {isUpdatingCategories ? (
                        <div
                            style={{
                                marginTop: "6px",
                                fontSize: "11px",
                                fontWeight: 600,
                                color: "#6e6b7b",
                            }}
                        >
                            جاري الحفظ...
                        </div>
                    ) : null}
                </div>
            </td>
        ),
        type: () => <td>{props.vendor.type === "restaurant" ? "مطعم" : "سوبر ماركت"}</td>,
        // Lifetime completed orders for this restaurant. The API counts these by
        // vendors.id, not by the users.id this row also carries — see
        // util/orderParticipantCounts.js.
        completedOrders: () => (
            <td>{Number(props.vendor?.completedOrders || 0).toLocaleString("en-US")}</td>
        ),
        // The rate agreed with this restaurant, or its city's default shown as
        // such. `order_percentage` is null for 223 of the 301 restaurants, so
        // most rows show the default and must say so.
        commissionPercentage: () => (
            <PercentageCell
                ownValue={props.vendor?.order_percentage}
                effectiveValue={props.vendor?.effective_order_percentage}
                onSave={(value) => props.onSavePercentage(props.vendor, value)}
            />
        ),
        // الترتيب — the pinned place in the customer lists, or «تلقائي».
        displayRank: () => (
            <DisplayRankCell
                rank={props.vendor?.displayRank}
                onSave={(rank) => props.onSaveDisplayRank(props.vendor, rank)}
            />
        ),
        // نقدي / محفظة (api#147) — the same cell the drivers table draws. A
        // service vendor is never charged, so the switch would mean nothing.
        walletMode: () => (
            <WalletModeCell
                enabled={props.vendor?.wallet?.enabled === true}
                onSave={(enabled) => props.onSaveWalletMode(props.vendor, enabled)}
                disabled={props.vendor?.isServiceVendor === true}
            />
        ),
        // The balance, and the closure when the wallet caused it. A button:
        // it opens the wallet, which is where the operator tops up.
        walletBalance: () => {
            const wallet = props.vendor?.wallet;
            const balance = Number(wallet?.balance ?? 0);

            return (
                // The same cell as the drivers table — see walletPanel.scss.
                <td className="wallet-cell">
                    <button
                        type="button"
                        className={`wallet-cell__value${balance < 0 ? " wallet-cell__value--debt" : ""}`}
                        onClick={() => props.onOpenWallet(props.vendor)}
                        title="عرض المحفظة"
                    >
                        {formatMoney(balance)}
                    </button>
                    {wallet?.blocked ? (
                        <span className="wallet-cell__chip">مغلق بسبب الرصيد</span>
                    ) : wallet?.enabled && !wallet?.effective ? (
                        <span className="wallet-cell__hint">المدينة غير مفعلة</span>
                    ) : null}
                </td>
            );
        },
        // Same control the people pages draw — see BlockStatusButton. The row's
        // `id` is the users.id, which is what the toggle endpoint reads.
        block: () => (
            <td className="cell--block">
                <BlockStatusButton account={props.vendor} onToggle={props.onToggleBlock} />
            </td>
        ),
        rating: () => (
            <td>
                {props.vendor?.ratingCount > 0 ? (
                    <span style={{ color: "#f5a623", fontWeight: 600, whiteSpace: "nowrap" }}>
                        ★ {Number(props.vendor.ratingAvg).toFixed(2)}{" "}
                        <span style={{ color: "#777", fontWeight: 400 }}>
                            ({props.vendor.ratingCount})
                        </span>
                    </span>
                ) : (
                    <span style={{ color: "#aaa" }}>—</span>
                )}
            </td>
        ),
        actions: () => (
            <td>
                <DropDownButtons
                    vendor={props.vendor}
                    edit={() => {
                        navigate(`${props.vendor.id}`);
                    }}
                    // Super admin only, same rule the endpoint enforces. A city
                    // admin offered the item would only ever get a 403, and
                    // removing a restaurant is not a per-city call to make.
                    delete={
                        userData?.super_admin
                            ? () => {
                                  props.setSelectedVendorValue(props.vendor);
                                  props.toggleDelete();
                              }
                            : null
                    }
                    changePassword={() => props.onChangePassword(props.vendor)}
                    // The restaurant's balance, closure reason, top-up and ledger (admin#41).
                    wallet={() => props.onOpenWallet(props.vendor)}
                    // Super admin only, the same rule the endpoint enforces.
                    // POST admin/impersonate/:userId carries requireSuperAdmin,
                    // which is a REAL guard and not a shadowed one, so a city
                    // admin offered the item would only ever read a 403.
                    impersonate={
                        userData?.super_admin ? () => props.onImpersonate(props.vendor) : null
                    }
                ></DropDownButtons>
            </td>
        ),
    };

    return (
        // An archived account is dimmed rather than hidden: it is listed only
        // so an operator can restore it. See table.scss for why the dimming is
        // colour and not opacity.
        <tr className={`item${props.vendor?.archivedAt ? " item--archived" : ""}`}>
            <TableBodyCells visibleColumns={props.visibleColumns} renderers={cellRenderers} />
        </tr>
    );
};

export default Vendor;
